import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  notification,
  Typography,
  Row,
  Col,
  Tag,
  Divider
} from 'antd';
import {
  EnvironmentOutlined,
  ClockCircleOutlined,
  CheckOutlined,
  CarOutlined,
  CalendarOutlined,
  FieldTimeOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';

import { useTheme } from '../../theme/ThemeProvider';
import './styles.css';

const { Title, Text } = Typography;

const TripDetails: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const [tripDetails, setTripDetails] = useState<any>(null);


  // Get tripDetails from location state or fetch from API
  useEffect(() => {
    if (location.state?.tripDetails) {
      setTripDetails(location.state.tripDetails);
    }
  }, [location.state]);

  // Helper function to get Persian name for trip type
  const getTripTypePersian = (type: number) => {
    switch (type) {
      case 1: return 'ترنسفر';
      case 2: return 'در اختیار';
      case 3: return 'بین شهری';
      default: return 'نامشخص';
    }
  };

  // Helper function to get Persian name for trip item type
  const getTripItemTypePersian = (type: number) => {
    switch (type) {
      case 0: return 'هیچ کدام';
      case 1: return 'مسافت';
      case 2: return 'مدت زمان';
      case 100: return 'محدوده ترافیک';
      case 101: return 'حومه شهر';
      case 102: return 'زمان توقف';
      case 103: return 'رفت و برگشت';
      default: return 'نامشخص';
    }
  };



  if (!tripDetails) {
    return (
      <div className="trip-details-container">
        <div className="trip-details-content">
          <Card className="card-themed">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <Text type="secondary" style={{ color: theme.text.secondary }}>اطلاعات سفر در دسترس نیست</Text>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="trip-details-container">
      <div className="trip-details-content">
        <Card className="card-themed">
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Title level={2} style={{ color: theme.text.primary }}>جزئیات سفر</Title>
            <Text type="secondary" style={{ color: theme.text.secondary }}>اطلاعات کامل سفر شما</Text>
          </div>

          {/* Trip Type */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <CarOutlined style={{ color: theme.primary }} />
                <Text strong>نوع سفر: </Text>
                <Tag color="blue">{getTripTypePersian(tripDetails.type)}</Tag>
              </div>
            </Col>
          </Row>

          <Divider />

          {/* Source and Destination */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <EnvironmentOutlined style={{ color: '#1890ff' }} />
                <Text strong>مبدا: </Text>
                <Text>{tripDetails.source?.address || 'آدرس مبدا'}</Text>
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <EnvironmentOutlined style={{ color: '#52c41a' }} />
                <Text strong>مقصد: </Text>
                <Text>{tripDetails.destination?.address || 'آدرس مقصد'}</Text>
              </div>
            </Col>
          </Row>

          <Divider />

          {/* Date and Time */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <CalendarOutlined style={{ color: '#faad14' }} />
                <Text strong>تاریخ شروع: </Text>
                <Text>{tripDetails.startDate ? new Date(tripDetails.startDate).toLocaleDateString('fa-IR') : 'تاریخ مشخص نشده'}</Text>
              </div>
            </Col>
            <Col span={24}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <FieldTimeOutlined style={{ color: '#faad14' }} />
                <Text strong>زمان شروع: </Text>
                <Text>{tripDetails.startTime || 'زمان مشخص نشده'}</Text>
              </div>
            </Col>
            {tripDetails.endDate && (
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <CalendarOutlined style={{ color: '#52c41a' }} />
                  <Text strong>تاریخ پایان: </Text>
                  <Text>{new Date(tripDetails.endDate).toLocaleDateString('fa-IR')}</Text>
                </div>
              </Col>
            )}
            {tripDetails.endTime && (
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                  <FieldTimeOutlined style={{ color: '#52c41a' }} />
                  <Text strong>زمان پایان: </Text>
                  <Text>{tripDetails.endTime}</Text>
                </div>
              </Col>
            )}
            {tripDetails.durationMinutes && (
              <Col span={24}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ClockCircleOutlined style={{ color: '#722ed1' }} />
                  <Text strong>مدت سفر: </Text>
                  <Text>{tripDetails.durationMinutes} دقیقه</Text>
                </div>
              </Col>
            )}
          </Row>

          <Divider />

          {/* Trip Items (Services) */}
          <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
            <Col span={24}>
              <Title level={4} style={{ marginBottom: 16 }}>خدمات انتخاب شده</Title>
              {tripDetails.tripItemRequests && tripDetails.tripItemRequests.length > 0 ? (
                <div>
                  {tripDetails.tripItemRequests.map((item: any, index: number) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      gap: 8, 
                      marginBottom: 8,
                      padding: '8px 12px',
                      backgroundColor: '#f8f9fa',
                      borderRadius: '6px',
                      border: '1px solid #e9ecef'
                    }}>
                      <Tag color="green" style={{ margin: 0 }}>
                        {getTripItemTypePersian(item.tripItemType)}
                      </Tag>
                      {item.count > 0 && (
                        <Text type="secondary">
                          تعداد: {item.count}
                          {item.tripItemType === 2 && ' ساعت'}
                          {item.tripItemType === 102 && ' دقیقه'}
                        </Text>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <Text type="secondary">هیچ خدمت خاصی انتخاب نشده است</Text>
              )}
            </Col>
          </Row>

          <Button
            type="primary"
            size="large"
            block
            icon={<CheckOutlined />}
            onClick={() => {
              notification.success({ message: 'سفر تایید شد' });
              navigate('/');
            }}
          >
            تایید سفر
          </Button>
        </Card>
      </div>
    </div>
  );
};

export default TripDetails; 